import React, { useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import Container from "@mui/material/Container";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Button from "@mui/material/Button";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { GET_PERMIT_DETAIL, GetPermitDetailData, GetPermitDetailVars } from "common/graphql/queries/GetPermitDetail";
import { getDocumentUrl } from "common/helpers/documentUrl";
import { useDocumentFetch } from "@app.automotus.io/components/hooks";
import { PermitDetailContent } from "./PermitDetailContent";
import { PermitDetailSkeleton } from "./PermitDetailSkeleton";
import type { Vehicle } from "./components/VehiclesSection";
import type { Document } from "./components/DocumentsSection";

/**
 * Container component for the Permit Detail page.
 * Handles data fetching, loading states, and error handling.
 */
export const PermitDetailPage: React.FC = () => {
  const { permitId } = useParams<{ permitId: string }>();
  const navigate = useNavigate();

  const { data, loading, error } = useQuery<GetPermitDetailData, GetPermitDetailVars>(GET_PERMIT_DETAIL, {
    variables: { id: permitId || "" },
    skip: !permitId,
    fetchPolicy: "cache-and-network",
  });

  const permit = data?.permit;
  const { fetchDocument } = useDocumentFetch();

  // Transform documents data from document proposals (memoized for handleViewDocument dependency)
  const documents: Document[] = useMemo(() => {
    if (!permit) {
      return [];
    }

    if (permit.documentProposals.length === 0) {
      return [];
    }

    return permit.documentProposals[0].documents.map((doc) => ({
      id: doc.documentId,
      name: doc.document.fileName,
      uploadedAt: doc.document.createdAt,
      documentType: doc.document.documentType?.name,
      s3Bucket: doc.document.s3Bucket,
      s3Path: doc.document.s3Path,
    }));
  }, [permit]);

  // Navigation handlers
  const handleBack = useCallback(() => {
    navigate("/permits");
  }, [navigate]);

  const handleStartNewApplication = useCallback(() => {
    navigate("/permits/apply");
  }, [navigate]);

  const handleViewDocument = useCallback(
    async (documentId: string) => {
      const doc = documents.find((d) => d.id === documentId);
      if (!doc) {
        return;
      }

      const url = getDocumentUrl(doc);
      if (!url) {
        return;
      }

      await fetchDocument({ url, action: "view", filename: doc.name });
    },
    [documents, fetchDocument],
  );

  // Loading state
  if (loading && !permit) {
    return (
      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 } }}>
        <PermitDetailSkeleton />
      </Container>
    );
  }

  // Error state
  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 } }}>
        <Button
          variant="text"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mb: 3, color: "text.secondary" }}
        >
          Back to Permits
        </Button>
        <Alert severity="error">
          <AlertTitle>Error Loading Permit</AlertTitle>
          {error.message || "An error occurred while loading the permit details. Please try again."}
        </Alert>
      </Container>
    );
  }

  // Not found state
  if (!permit) {
    return (
      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 } }}>
        <Button
          variant="text"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mb: 3, color: "text.secondary" }}
        >
          Back to Permits
        </Button>
        <Alert severity="warning">
          <AlertTitle>Permit Not Found</AlertTitle>
          The permit you are looking for could not be found.
        </Alert>
      </Container>
    );
  }

  // Transform vehicles data
  const vehicles: Vehicle[] = permit.vehicles.map((v) => ({
    id: v.registeredVehicleId,
    licensePlate: v.registeredVehicle.licensePlate,
    state: v.registeredVehicle.stateCode || undefined,
    make: v.registeredVehicle.make || undefined,
    model: v.registeredVehicle.model || undefined,
  }));

  // Get the latest review comment from the most recent document proposal
  const latestReviewComment = permit.documentProposals.length > 0 ? permit.documentProposals[0].reviewComment : null;

  const issuingAuthority = permit.authority?.name || "City Authority";

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 } }}>
      <PermitDetailContent
        permitId={permit.id}
        displayId={permit.displayId}
        permitName={permit.permitType.name}
        permitStatus={permit.status}
        applicationStatus={permit.applicationStatus}
        permitType={permit.permitType.name}
        submittedDate={permit.applicationSubmittedAt}
        approvedDate={permit.applicationReviewedAt}
        expiryDate={permit.validTo}
        issuingAuthority={issuingAuthority}
        reviewComment={latestReviewComment}
        vehicles={vehicles}
        documents={documents}
        vehicleCount={vehicles.length}
        onBack={handleBack}
        onStartNewApplication={handleStartNewApplication}
        onViewDocument={handleViewDocument}
      />
    </Container>
  );
};

export default PermitDetailPage;
